import React from 'react';
import { Field } from '@api';
import { ApiDocumentType } from '../api/documentTypes';
import { ApiFolderLite, getChildFolders, getRootFolders } from '../api/folders';
import { Modal } from './seachpage-modal';
import { getInputType, isAmountField, isDateField } from './seachpage-helpers';
import './EditDocumentModal.css';

export interface EditDocumentModalProps {
  doc: any;
  documentFields: Field[];
  documentTypes: ApiDocumentType[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  onCancel: () => void;
  onSave: () => void;
  saving?: boolean;
  error?: string | null;

  /**
   * edit = existing document; create = importing a new file via ImportIC
   */
  mode?: 'edit' | 'create';
  fileName?: string;
  drawerId?: number | null;
  defaultFolderId?: number | null;
}

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  doc,
  documentFields,
  documentTypes,
  values,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
  mode = 'edit',
  fileName,
  drawerId,
  defaultFolderId
}) => {
  const isCreate = mode === 'create';

  const currentDocTypeId = doc?.documentTypeId ?? doc?.DocumentTypeId ?? doc?.documenttypeid;
  const effectiveDocumentTypeId = (
    isCreate
      ? (values.documentTypeId ?? '')
      : (values.documentTypeId ?? currentDocTypeId ?? (documentTypes?.[0]?.id ?? ''))
  ) as any;

  function getExtFromName(name: string | null | undefined): string {
    const s = (name || '').toString();
    const m = s.match(/\.([a-z0-9]+)$/i);
    return (m?.[1] || '').toLowerCase();
  }

  function normalizeDocTypeExt(extRaw: any): string[] {
    const s = (extRaw || '').toString().toLowerCase().trim();
    if (!s) return [];
    return s
      .split(/[;,\s]+/g)
      .map(x => x.trim())
      .filter(Boolean)
      .map(x => (x.startsWith('.') ? x.slice(1) : x));
  }

  const fileExt = React.useMemo(() => {
    if (isCreate) return getExtFromName(fileName);

    const extFromDoc = (
      doc?.extension ||
      doc?.Extension ||
      doc?.documentTypeExtension ||
      doc?.DocumentTypeExtension ||
      getExtFromName(doc?.fileName ?? doc?.FileName ?? doc?.filename ?? doc?.Filename)
    ) as string;
    const normalized = (extFromDoc || '').toString().toLowerCase().trim().replace(/^\./, '');
    if (normalized) return normalized;

    // Fallback: infer extension from the currently selected document type.
    const curId = Number(effectiveDocumentTypeId);
    const cur = (Array.isArray(documentTypes) ? documentTypes : []).find(dt => Number(dt.id) === curId);
    const list = normalizeDocTypeExt(cur?.documentTypeExtension || (cur as any)?.DocumentTypeExtension);
    if (list.length === 1) return list[0] as string;
    return '';
  }, [isCreate, fileName, doc, documentTypes, effectiveDocumentTypeId]);

  const filteredDocumentTypes: ApiDocumentType[] = React.useMemo(() => {
    const all = Array.isArray(documentTypes) ? documentTypes : [];
    const ext = (fileExt || '').toLowerCase().trim();
    if (!isCreate) return all;
    if (!ext) return all;

    const matches = all.filter(dt => {
      const list = normalizeDocTypeExt(dt.documentTypeExtension || (dt as any).DocumentTypeExtension);
      return list.includes(ext);
    });

    // Ensure current selection remains visible even if metadata is inconsistent.
    const currentId = Number(effectiveDocumentTypeId);
    const current = all.find(dt => Number(dt.id) === currentId);
    if (current && !matches.some(dt => Number(dt.id) === currentId)) return [current, ...matches];
    return matches;
  }, [documentTypes, fileExt, effectiveDocumentTypeId, isCreate]);

  const noDocumentTypeAvailable = isCreate && (filteredDocumentTypes?.length ?? 0) === 0;

  const [folders, setFolders] = React.useState<ApiFolderLite[]>([]);
  const [foldersError, setFoldersError] = React.useState<string | null>(null);
  const [subfolders, setSubfolders] = React.useState<ApiFolderLite[]>([]);
  const [subfoldersError, setSubfoldersError] = React.useState<string | null>(null);

  const selectedFolderId: number | null = isCreate ? (values.folderId ?? defaultFolderId ?? null) : null;
  const effectiveFolderId: number | '' = isCreate ? (selectedFolderId ?? '') : '';

  React.useEffect(() => {
    if (!isCreate) return;
    if (!drawerId) return;
    let cancelled = false;
    setFoldersError(null);
    (async () => {
      try {
        const data = await getRootFolders(drawerId);
        if (cancelled) return;
        setFolders(data);
      } catch (e: any) {
        if (cancelled) return;
        setFolders([]);
        setFoldersError(e?.message || 'Failed to load folders');
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, drawerId]);

  React.useEffect(() => {
    if (!isCreate) return;
    if (!drawerId) return;
    if (!selectedFolderId) { setSubfolders([]); setSubfoldersError(null); return; }
    let cancelled = false;
    setSubfoldersError(null);
    (async () => {
      try {
        const data = await getChildFolders(drawerId, selectedFolderId);
        if (cancelled) return;
        setSubfolders(data);
      } catch (e: any) {
        if (cancelled) return;
        setSubfolders([]);
        setSubfoldersError(e?.message || 'Failed to load subfolders');
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, drawerId, selectedFolderId]);

  // Build ordered fields using the provided array (assumed to be in endpoint order).
  // In edit mode we intentionally hide Subfolder (user requested).
  const orderedFields: Field[] = React.useMemo(() => {
    const arr = Array.isArray(documentFields) ? documentFields.slice() : [];
    if (isCreate) return arr;
    return arr.filter(f => (f?.name || '').toLowerCase() !== 'subfolder');
  }, [documentFields, isCreate]);

  return (
    // Increased width and maxHeight
    <Modal title={isCreate ? 'Add document' : `Edit Document #${doc?.id ?? ''}`} onClose={onCancel} width={700} showClose={false}>
      {error && <div className="edit-document-modal__error">{error}</div>}
      <div className="edit-document-modal__container">
        {isCreate && (
          <div className="edit-document-modal__description-section">
            <span className="edit-document-modal__field-label">File</span>
            <input value={fileName || ''} readOnly className="edit-document-modal__input" />
          </div>
        )}
        <div className="edit-document-modal__description-section">
          <span className="edit-document-modal__field-label">Document type</span>
          <select
            value={effectiveDocumentTypeId}
            onChange={e => {
              const raw = (e.target as HTMLSelectElement).value;
              onChange({ ...values, documentTypeId: Number(raw) });
            }}
            className="edit-document-modal__input"
            disabled={noDocumentTypeAvailable}
          >
            {noDocumentTypeAvailable ? (
              <option value="" disabled>No document types available</option>
            ) : (
              (filteredDocumentTypes || []).map(dt => {
                const name = dt.documentTypeName || dt.DocumentTypeName || String(dt.id);
                const ext = (dt.documentTypeExtension || dt.DocumentTypeExtension || '').toString().trim();
                const label = ext ? `${name} (${ext})` : name;
                return (
                  <option key={dt.id} value={dt.id}>{label}</option>
                );
              })
            )}
          </select>
          {noDocumentTypeAvailable && (
            <div className="edit-document-modal__error">
              No document type available for the type of file you are archiving.
            </div>
          )}
        </div>

        {isCreate && (
          <>
            <div className="edit-document-modal__description-section">
              <span className="edit-document-modal__field-label">Folder *</span>
              <select
                value={effectiveFolderId}
                onChange={e => {
                  const v = (e.target as HTMLSelectElement).value;
                  const nextId = v ? Number(v) : null;
                  onChange({ ...values, folderId: nextId, subfoldername: '' });
                }}
                className="edit-document-modal__input"
              >
                <option value="">Select a folder…</option>
                {folders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              {foldersError && <div className="edit-document-modal__error">{foldersError}</div>}
            </div>

            <div className="edit-document-modal__description-section">
              <span className="edit-document-modal__field-label">Subfolder</span>
              <select
                value={values.subfoldername ?? ''}
                onChange={e => {
                  const v = (e.target as HTMLSelectElement).value;
                  onChange({ ...values, subfoldername: v });
                }}
                className="edit-document-modal__input"
                disabled={!selectedFolderId}
              >
                <option value=""></option>
                {subfolders.map(sf => (
                  <option key={sf.id} value={sf.name}>{sf.name}</option>
                ))}
              </select>
              {subfoldersError && <div className="edit-document-modal__error">{subfoldersError}</div>}
            </div>
          </>
        )}

        <div className="edit-document-modal__description-section">
          <span className="edit-document-modal__field-label">Description</span>
          <input 
            value={values.description||''} 
            onChange={e=> onChange({ ...values, description: e.target.value })} 
            className="edit-document-modal__input" 
          />
        </div>
        <div className="edit-document-modal__fields-container">
          <div className="edit-document-modal__fields-grid">
            {orderedFields.map(field=> {
              const k = field.name;
              const inputType = getInputType(field as Field);
              const isCheckbox = inputType === 'checkbox';
              const isCurrency = isAmountField(field as Field);
              const isDate = isDateField(field as Field);
              const raw = values[k];
              const rawStr = typeof raw === 'string' ? raw : (raw==null ? '' : String(raw));

              if (!isCreate && k.toLowerCase() === 'subfolder') return null;

              let valueForInput: string;
              if (inputType === 'date') {
                valueForInput = rawStr?.split('T')[0] ?? '';
              } else {
                valueForInput = rawStr;
              }
               
              // Choose appropriate CSS class
              const inputClass = isDate ? 'edit-document-modal__input--date' : 'edit-document-modal__input';
               
              return (
                <React.Fragment key={k}>
                  <span className="edit-document-modal__field-label">{(field as Field).title ?? k}</span>
                  {isCheckbox ? (
                    <input
                      type="checkbox"
                      checked={rawStr.toLowerCase() === 'true'}
                      onChange={e=> onChange({ ...values, [k]: e.target.checked ? 'true' : 'false' })}
                    />
                  ) : Array.isArray((field as any).options) && (field as any).options.length > 0 && inputType === 'text' ? (
                    <select
                      value={valueForInput ?? ''}
                      onChange={e=> {
                        const v = (e.target as HTMLSelectElement).value;
                        onChange({ ...values, [k]: v });
                      }}
                      className={inputClass}
                    >
                      <option value=""></option>
                      {((field as any).options as string[]).map((opt, idx) => (
                        <option key={idx} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={isCurrency ? 'text' : inputType}
                      inputMode={isCurrency ? 'decimal' : undefined}
                      value={valueForInput ?? ''}
                      onChange={e=> {
                        const inputValue = (e.target as HTMLInputElement).value;
                        onChange({ ...values, [k]: inputValue });
                      }}
                      className={inputClass}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className="edit-document-modal__actions">
          <button 
            type='button' 
            onClick={onCancel} 
            className="edit-document-modal__button edit-document-modal__button--cancel"
          >
            Cancel
          </button>
          <button 
            type='button' 
            disabled={!!saving || noDocumentTypeAvailable} 
            onClick={onSave} 
            className="edit-document-modal__button edit-document-modal__button--save"
          >
            {saving? 'Saving...':'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
